import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import usePagination10 from './components/hooks/usePagination10';
import PageCard from './components/PageCard';

function SessionsAtivas() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroGeral, setFiltroGeral] = useState('');
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios.post(
      API_BASE_URL.SESSIONS_ACTIVE,
      null,
      { suppressAuthRedirect: true }
    )
      .then(res => {
        const parseLoginTime = (s) => {
          const t = s?.loginTime ?? s?.LOGIN_TIME ?? s?.login_time;
          if (Array.isArray(t) && t.length >= 6) {
            const [year, month, day, hour, minute, second] = t.map(v => Number(v));
            return new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, second || 0).getTime();
          }
          if (typeof t === 'string') {
            const normalized = t.replace(' ', 'T');
            const p = Date.parse(normalized);
            return isNaN(p) ? 0 : p;
          }
          return 0;
        };
        const sorted = Array.isArray(res.data)
          ? res.data.slice().sort((a, b) => parseLoginTime(b) - parseLoginTime(a))
          : res.data;
        setSessions(sorted);
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const filteredSessions = Array.isArray(sessions)
    ? sessions.filter(s => {
        if (!filtroGeral) return true;
        const busca = normalizeString(filtroGeral);
        return (
          normalizeString(s.login || s.cpf || '').includes(busca) ||
          normalizeString(s.token || '').includes(busca) ||
          normalizeString(s.location || '').includes(busca)
        );
      })
    : [];

  const totalItems = filteredSessions.length;
  const { setPagination } = usePagination10(totalItems);
  const pageSessions = filteredSessions.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const formatTime = (t) => {
    if (Array.isArray(t) && t.length >= 6) {
      return `${String(t[2]).padStart(2,'0')}/${String(t[1]).padStart(2,'0')}/${t[0]} ${String(t[3]).padStart(2,'0')}:${String(t[4]).padStart(2,'0')}:${String(t[5]).padStart(2,'0')}`;
    }
    if (typeof t === 'string' && t.includes('-')) {
      const parts = t.split(/[\sT]/);
      const dateParts = parts[0]?.split('-');
      if (dateParts && dateParts.length === 3) {
        return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]} ${parts[1] || ''}`;
      }
    }
    return t ?? '-';
  };

  if (loading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="SESSÕES ATIVAS"
      icon="bi-list-task"
      scrollable={false}
      footer={totalItems > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle" style={{ width: '5%' }}>Nº</th>
                <th className="px-2 align-middle">Login</th>
                <th className="px-2 align-middle">Token</th>
                <th className="px-2 align-middle">Localização</th>
                <th className="px-2 align-middle">Horário de Login</th>
              </tr>
            </thead>
            <tbody>
              {pageSessions.map((sess, idx) => (
                <tr key={sess.id || idx}>
                  <td className="px-2">{setPagination.startIndex + idx + 1}</td>
                  <td className="px-2">{sess.login ?? sess.cpf ?? '-'}</td>
                  <td className="px-2" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sess.token ?? '-'}
                  </td>
                  <td className="px-2">{sess.location && sess.location !== 'null, null, null' ? sess.location : '-'}</td>
                  <td className="px-2">{formatTime(sess.loginTime)}</td>
                </tr>
              ))}
              {filteredSessions.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-3">
                    {sessions.length === 0 ? 'Nenhuma sessão ativa encontrada.' : 'Nenhum registro corresponde à pesquisa.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageCard>
  );
}

export default SessionsAtivas;
